import React, { useState, useEffect } from 'react';
import type { UserProfile } from '../../api/profile';
import { updateUserProfile, adminSetUserPassword } from '../../api/profile';
import type { Role } from '../../features/roles/types';
import { ApiError } from '../../shared/api';

interface EditUserModalProps {
  show: boolean;
  onHide: () => void;
  user: UserProfile;
  roles: Role[];
  onUserUpdated: () => void;
  /** When false, role is display-only and roleId is not sent (users.edit without users.admin). */
  canChangeRole?: boolean;
  /** System ADMIN only — show email + change-password flow */
  allowPasswordReset?: boolean;
  /** True when editing the signed-in user (password change will sign them out of other sessions) */
  isEditingSelf?: boolean;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  show,
  onHide,
  user,
  roles,
  onUserUpdated,
  canChangeRole = true,
  allowPasswordReset = false,
  isEditingSelf = false,
}) => {
  const [formData, setFormData] = useState({ name: user.name, phone: user.phone || '', roleId: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pwForm, setPwForm] = useState({ password: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);

  useEffect(() => {
    const fallbackRoleId = roles.find((role) => role.displayName === user.role)?.id ?? '';
    setFormData({
      name: user.name,
      phone: user.phone || '',
      roleId: user.roleId ?? fallbackRoleId,
    });
  }, [user, roles]);

  useEffect(() => {
    if (!show) {
      setShowPasswordModal(false);
      setPwForm({ password: '', confirm: '' });
      setPwError(null);
    }
  }, [show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      if (canChangeRole && !formData.roleId) {
        setError('Please select a role');
        setIsLoading(false);
        return;
      }
      await updateUserProfile(user.userId, {
        name: formData.name,
        phone: formData.phone,
        ...(canChangeRole && formData.roleId ? { roleId: formData.roleId } : {}),
      });
      await onUserUpdated();
      onHide();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError(null);
    if (pwForm.password.length < 8) {
      setPwError('Password must be at least 8 characters.');
      return;
    }
    if (pwForm.password !== pwForm.confirm) {
      setPwError('Passwords do not match.');
      return;
    }
    setPwLoading(true);
    try {
      await adminSetUserPassword(user.userId ?? user.id, pwForm.password);
      setShowPasswordModal(false);
      setPwForm({ password: '', confirm: '' });
      await onUserUpdated();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          setPwError('Only system administrators can set passwords.');
        } else {
          setPwError(err.message || 'Failed to update password');
        }
      } else {
        setPwError(err instanceof Error ? err.message : 'Failed to update password');
      }
    } finally {
      setPwLoading(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header bg-primary text-white">
              <h5 className="modal-title">
                <i className="bi bi-person-gear me-2"></i>Edit User
              </h5>
              <button type="button" className="btn-close btn-close-white" onClick={onHide} disabled={isLoading} />
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}
                <div className="mb-3">
                  <label htmlFor="editUserEmail" className="form-label">
                    Email
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    id="editUserEmail"
                    value={user.email}
                    disabled
                    readOnly
                    autoComplete="off"
                  />
                  <div className="form-text">Email is read-only. Contact support if it must be changed.</div>
                </div>
                <div className="mb-3">
                  <label htmlFor="editUserName" className="form-label">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="editUserName"
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="editUserPhone" className="form-label">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    id="editUserPhone"
                    value={formData.phone}
                    onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                {allowPasswordReset && (
                  <div className="mb-3">
                    <span className="form-label d-block">Password</span>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => {
                        setPwForm({ password: '', confirm: '' });
                        setPwError(null);
                        setShowPasswordModal(true);
                      }}
                    >
                      <i className="bi bi-key me-1" aria-hidden />
                      Change password…
                    </button>
                    <div className="form-text">Only system administrators can set passwords.</div>
                  </div>
                )}
                <div className="mb-3">
                  <span className="form-label d-block">Role</span>
                  {canChangeRole ? (
                    <select
                      id="editUserRole"
                      className="form-select"
                      value={formData.roleId}
                      onChange={(e) => setFormData((prev) => ({ ...prev, roleId: e.target.value }))}
                      required
                    >
                      {roles.length === 0 ? (
                        <option value="">No roles available</option>
                      ) : (
                        roles.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.displayName}
                          </option>
                        ))
                      )}
                    </select>
                  ) : (
                    <p className="form-control-plaintext border rounded px-3 py-2 mb-0 bg-light">{user.role}</p>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onHide} disabled={isLoading}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check me-2"></i>Update User
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {showPasswordModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1060 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="changePwTitle"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="changePwTitle">
                  <i className="bi bi-key me-2" />
                  Set password
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPasswordModal(false)}
                  disabled={pwLoading}
                  aria-label="Close"
                />
              </div>
              <form onSubmit={handlePasswordSubmit}>
                <div className="modal-body">
                  {pwError && (
                    <div className="alert alert-danger" role="alert">
                      {pwError}
                    </div>
                  )}
                  {isEditingSelf && (
                    <p className="small text-muted">
                      Your other sessions will be signed out after the password is changed. Stay signed in on this
                      device with the new password.
                    </p>
                  )}
                  <div className="mb-3">
                    <label htmlFor="newPw" className="form-label">
                      New password
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="newPw"
                      value={pwForm.password}
                      onChange={(e) => setPwForm((p) => ({ ...p, password: e.target.value }))}
                      minLength={8}
                      autoComplete="new-password"
                      required
                    />
                  </div>
                  <div className="mb-0">
                    <label htmlFor="confirmPw" className="form-label">
                      Confirm password
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="confirmPw"
                      value={pwForm.confirm}
                      onChange={(e) => setPwForm((p) => ({ ...p, confirm: e.target.value }))}
                      minLength={8}
                      autoComplete="new-password"
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowPasswordModal(false)}
                    disabled={pwLoading}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={pwLoading}>
                    {pwLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        Saving…
                      </>
                    ) : (
                      'Save password'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditUserModal;
